// ═══════════════════════════════════════════════════════════
// PII RULES ENGINE — Table-driven inference & impact analysis
// Pure logic. No React. No UI. Fully testable and expandable.
// ═══════════════════════════════════════════════════════════

// ──────────────────────────────────────────────────────────
// 1. DATA CATEGORY DEFINITIONS
// Business-friendly personal data categories
// ──────────────────────────────────────────────────────────

export const DATA_CATEGORIES = {
  identity:    { id: 'identity',    label: 'Identity Data',           short: 'Names, DOB, gender, nationality, government IDs',            color: '#003366' },
  contact:     { id: 'contact',     label: 'Contact Data',            short: 'Email, phone, postal address, social media handles',          color: '#1E6091' },
  financial:   { id: 'financial',   label: 'Financial Data',          short: 'Bank accounts, UPI IDs, cards, salary, loans, credit scores', color: '#DC2626' },
  employment:  { id: 'employment',  label: 'Employment Data',         short: 'Job title, employee ID, salary slips, performance records',   color: '#7C3AED' },
  technical:   { id: 'technical',   label: 'Technical Data',          short: 'IP addresses, device IDs, cookies, browser fingerprints',     color: '#0891B2' },
  behavioral:  { id: 'behavioral',  label: 'Behavioral Data',         short: 'Purchase history, browsing patterns, preferences, ad clicks', color: '#F59E0B' },
  image_voice: { id: 'image_voice', label: 'Image & Voice Data',      short: 'Photos, CCTV footage, call recordings, facial images',       color: '#DB2777' },
  location:    { id: 'location',    label: 'Location Data',           short: 'GPS coordinates, delivery addresses, travel records',         color: '#059669' },
  children:    { id: 'children',    label: 'Children-Related Data',   short: 'Any personal data of individuals under 18 years',            color: '#EF4444' },
  sensitive:   { id: 'sensitive',   label: 'Sensitive-Context Data',  short: 'Health records, biometric templates, caste, criminal records',color: '#B91C1C' },
};

// ──────────────────────────────────────────────────────────
// 2. INFERENCE LOOKUP TABLES
// Each selection maps to likely data categories
// ──────────────────────────────────────────────────────────

const INDUSTRY_CATEGORY_MAP = {
  bfsi:          ['identity', 'contact', 'financial', 'employment', 'technical'],
  healthcare:    ['identity', 'contact', 'sensitive', 'employment', 'location'],
  it_saas:       ['identity', 'contact', 'technical', 'behavioral'],
  manufacturing: ['identity', 'contact', 'employment', 'location'],
  retail:        ['identity', 'contact', 'financial', 'behavioral', 'location'],
  education:     ['identity', 'contact', 'children', 'employment'],
  logistics:     ['identity', 'contact', 'location', 'technical', 'employment'],
  real_estate:   ['identity', 'contact', 'financial', 'location'],
  professional_services: ['identity', 'contact', 'financial', 'employment'],
};

const ACTIVITY_CATEGORY_MAP = {
  customer_kyc:     ['identity', 'contact', 'financial', 'image_voice'],
  employee_payroll: ['identity', 'contact', 'financial', 'employment'],
  marketing:        ['contact', 'behavioral', 'technical', 'location'],
  payments:         ['identity', 'financial', 'technical'],
  loyalty:          ['identity', 'contact', 'behavioral', 'financial'],
  vendor_mgmt:      ['identity', 'contact', 'financial'],
};

const SUBJECT_CATEGORY_MAP = {
  customers:  ['identity', 'contact', 'financial', 'behavioral'],
  employees:  ['identity', 'contact', 'employment', 'financial'],
  applicants: ['identity', 'contact', 'employment'],
  vendors:    ['identity', 'contact', 'financial'],
  visitors:   ['technical', 'behavioral', 'location'],
  children:   ['children', 'identity', 'contact'],
};

const ELEMENT_CATEGORY_MAP = {
  contact_info: ['identity', 'contact'],
  gov_ids:      ['identity', 'sensitive'],
  financial:    ['financial'],
  health:       ['sensitive'],
  biometric:    ['sensitive', 'image_voice'],
  digital:      ['technical', 'behavioral', 'location'],
};

const SYSTEM_CATEGORY_MAP = {
  crm:    ['contact', 'behavioral', 'identity'],
  hrms:   ['employment', 'identity', 'financial'],
  cloud:  ['technical', 'location'],
  saas:   ['technical', 'behavioral'],
  onprem: ['identity', 'employment'],
  sheets: ['identity', 'contact', 'financial'],
};

// ──────────────────────────────────────────────────────────
// 3. IMPACT FLAG DEFINITIONS & TRIGGER RULES
// ──────────────────────────────────────────────────────────

export const IMPACT_FLAG_DEFS = {
  notice: {
    id: 'notice',
    label: 'Notice Requirement',
    dpdpRef: 'Section 5 — Notice to Data Principal',
    description: 'You likely need to issue a clear, itemised notice to every person whose data you process — explaining what data you collect, why, and how long you retain it.',
  },
  consent: {
    id: 'consent',
    label: 'Consent Dependency',
    dpdpRef: 'Section 4 — Consent',
    description: 'Most of your data processing likely depends on obtaining explicit, informed, and freely given consent. Consent records must be auditable and revocable.',
  },
  legitimate_use: {
    id: 'legitimate_use',
    label: 'Legitimate Business Processing',
    dpdpRef: 'Section 7 — Certain Legitimate Uses',
    description: 'Some processing (employment, statutory compliance, legal claims) may qualify under "legitimate uses" without consent — but must be specifically documented.',
  },
  processor_oversight: {
    id: 'processor_oversight',
    label: 'Processor & Vendor Oversight',
    dpdpRef: 'Section 8 — Obligations of Data Processors',
    description: 'Third-party tools and vendors processing personal data on your behalf must operate under binding contracts with audit rights and data handling limits.',
  },
  retention_deletion: {
    id: 'retention_deletion',
    label: 'Retention & Deletion Need',
    dpdpRef: 'Section 8(7) — Erasure and Retention',
    description: 'Every system storing personal data needs a defined retention period. Data must be provably deleted when the purpose is fulfilled or consent is withdrawn.',
  },
  grievance_rights: {
    id: 'grievance_rights',
    label: 'Grievance & Rights Handling',
    dpdpRef: 'Section 8 — Rights of Data Principal',
    description: 'Data principals have the right to access, correct, erase their data, and file grievances. You need a documented process and a designated point of contact.',
  },
  breach_exposure: {
    id: 'breach_exposure',
    label: 'Data Breach Exposure',
    dpdpRef: 'Section 8(6) — Breach Notification',
    description: 'The personal data you handle creates material breach notification obligations. You must notify the Data Protection Board and affected individuals without delay.',
  },
  children_caution: {
    id: 'children_caution',
    label: "Children's Data Caution",
    dpdpRef: 'Section 9 — Processing of Children\'s Data',
    description: "Processing children's data triggers verifiable parental consent requirements and a prohibition on tracking, profiling, or behavioural monitoring of minors.",
  },
  high_volume_risk: {
    id: 'high_volume_risk',
    label: 'High-Volume / Regulated-Sector Risk',
    dpdpRef: 'Section 10 — Significant Data Fiduciary',
    description: 'Your industry or data volume indicators suggest you may be classified as a Significant Data Fiduciary — requiring a DPO, periodic DPIA, and enhanced audit obligations.',
  },
};

// Trigger rules: each flag has conditions and a severity calculator
const FLAG_TRIGGER_RULES = {
  notice: {
    triggers: (sel, cats) => sel.subjects.length > 0 || cats.length > 0,
    severity: (sel, cats) => cats.length >= 5 ? 'high' : cats.length >= 3 ? 'medium' : 'low',
  },
  consent: {
    triggers: (sel, cats) => sel.elements.length > 0 || sel.activities.length > 0,
    severity: (sel, cats) => {
      const hasCriticalCat = cats.some(c => ['sensitive', 'financial', 'children'].includes(c.id));
      return hasCriticalCat ? 'high' : sel.elements.length >= 3 ? 'medium' : 'low';
    },
  },
  legitimate_use: {
    triggers: (sel) => ['employee_payroll', 'payments', 'vendor_mgmt'].some(a => sel.activities.includes(a)),
    severity: (sel) => sel.activities.includes('employee_payroll') && sel.activities.includes('payments') ? 'medium' : 'low',
  },
  processor_oversight: {
    triggers: (sel) => ['cloud', 'saas', 'sheets'].some(s => sel.systems.includes(s)) || sel.activities.includes('vendor_mgmt'),
    severity: (sel) => {
      const extCount = ['cloud', 'saas'].filter(s => sel.systems.includes(s)).length;
      return (extCount >= 2 || sel.activities.includes('vendor_mgmt')) ? 'high' : 'medium';
    },
  },
  retention_deletion: {
    triggers: (sel) => sel.systems.length > 0 || sel.elements.length >= 2,
    severity: (sel) => {
      const hasSheets = sel.systems.includes('sheets');
      return (sel.systems.length >= 3 || hasSheets) ? 'high' : 'medium';
    },
  },
  grievance_rights: {
    triggers: (sel) => ['customers', 'employees', 'applicants', 'children'].some(s => sel.subjects.includes(s)),
    severity: (sel) => sel.subjects.includes('children') ? 'high' : sel.subjects.length >= 3 ? 'medium' : 'low',
  },
  breach_exposure: {
    triggers: (sel, cats) => cats.some(c => ['sensitive', 'financial', 'identity'].includes(c.id)),
    severity: (sel, cats) => {
      const hasSensitive = cats.some(c => c.id === 'sensitive');
      const hasFinancial = cats.some(c => c.id === 'financial');
      return (hasSensitive && hasFinancial) ? 'high' : (hasSensitive || hasFinancial) ? 'medium' : 'low';
    },
  },
  children_caution: {
    triggers: (sel, cats) => sel.subjects.includes('children') || cats.some(c => c.id === 'children'),
    severity: () => 'high',
  },
  high_volume_risk: {
    triggers: (sel, cats) => {
      const indRisk = { bfsi: 4, healthcare: 5, it_saas: 3, manufacturing: 2, retail: 3, education: 4 };
      const risk = indRisk[sel.industry] || 0;
      return risk >= 4 || sel.subjects.length >= 4 || cats.some(c => c.id === 'sensitive');
    },
    severity: (sel, cats) => {
      const indRisk = { bfsi: 4, healthcare: 5, it_saas: 3, manufacturing: 2, retail: 3, education: 4 };
      const hasSensitive = cats.some(c => c.id === 'sensitive');
      const risk = indRisk[sel.industry] || 0;
      return (risk >= 5 || (risk >= 4 && hasSensitive)) ? 'high' : 'medium';
    },
  },
};

// ──────────────────────────────────────────────────────────
// 4. BLIND SPOT RULES (table-driven)
// ──────────────────────────────────────────────────────────

const BLIND_SPOT_RULES = [
  {
    id: 'sheets_untracked',
    condition: (sel) => sel.systems.includes('sheets'),
    text: 'Spreadsheets and shared drives are the #1 source of untracked personal data. Under DPDP, every storage location must be inventoried and access-controlled.',
    severity: 'high',
  },
  {
    id: 'visitor_cookies',
    condition: (sel) => sel.subjects.includes('visitors') && !sel.elements.includes('digital'),
    text: 'Website visitors generate cookies, IP logs, and device fingerprints — all classified as personal data under DPDP, even without user login or account creation.',
    severity: 'medium',
  },
  {
    id: 'payroll_govids',
    condition: (sel) => sel.activities.includes('employee_payroll') && !sel.elements.includes('gov_ids'),
    text: 'Payroll processing almost always involves Aadhaar, PAN, and bank details — high-risk data elements that need separate consent records and restricted access.',
    severity: 'high',
  },
  {
    id: 'cloud_crossborder',
    condition: (sel) => (sel.systems.includes('cloud') || sel.systems.includes('saas')) && !sel.activities.includes('vendor_mgmt'),
    text: 'Cloud and SaaS platforms may store data outside India. If your provider has foreign data centres, cross-border transfer rules under Section 16 kick in — even for routine business tools.',
    severity: 'high',
  },
  {
    id: 'marketing_visitors',
    condition: (sel) => sel.activities.includes('marketing') && !sel.subjects.includes('visitors'),
    text: 'Marketing tools track website visitors through cookies, pixels, and UTM parameters. These are personal data under DPDP regardless of whether visitors create an account.',
    severity: 'medium',
  },
  {
    id: 'children_online',
    condition: (sel) => sel.subjects.length >= 3 && !sel.subjects.includes('children'),
    text: 'If your product or service is accessible online, children under 18 may be using it. DPDP requires verifiable parental consent for any processing of children\'s data.',
    severity: 'medium',
  },
  {
    id: 'kyc_image_voice',
    condition: (sel, cats) => sel.activities.includes('customer_kyc') && !cats.some(c => c.id === 'image_voice'),
    text: 'KYC processes often capture selfies, video-KYC recordings, or photo IDs — this qualifies as image data and may trigger biometric-level obligations under DPDP.',
    severity: 'medium',
  },
  {
    id: 'crm_behavioral',
    condition: (sel) => sel.systems.includes('crm') && !sel.activities.includes('loyalty'),
    text: 'CRM tools automatically track interactions, deal stages, and communication history. This behavioural profiling data requires purpose limitation under DPDP.',
    severity: 'low',
  },
  {
    id: 'multi_system_retention',
    condition: (sel) => sel.systems.length >= 3,
    text: 'Personal data across 3+ systems makes consistent retention and deletion extremely difficult. Each system needs mapped retention periods and provable purge workflows.',
    severity: 'high',
  },
  {
    id: 'hrms_sensitive',
    condition: (sel) => sel.systems.includes('hrms') && !sel.elements.includes('health'),
    text: 'HRMS platforms commonly store medical leave records, insurance claims, and disability data — classified as sensitive personal data requiring heightened safeguards.',
    severity: 'medium',
  },
];

// ──────────────────────────────────────────────────────────
// 4b. TOUCHPOINT INFERENCE TABLE
// Maps user selections to likely business touchpoints
// ──────────────────────────────────────────────────────────

const TOUCHPOINT_RULES = {
  collect: [
    { condition: (sel) => sel.activities.includes('customer_kyc'), label: 'Customer onboarding & KYC forms' },
    { condition: (sel) => sel.activities.includes('employee_payroll'), label: 'Hiring paperwork & offer letters' },
    { condition: (sel) => sel.activities.includes('marketing'), label: 'Website forms, landing pages & tracking pixels' },
    { condition: (sel) => sel.activities.includes('payments'), label: 'Payment checkout & billing flows' },
    { condition: (sel) => sel.activities.includes('loyalty'), label: 'Loyalty sign-ups & feedback surveys' },
    { condition: (sel) => sel.activities.includes('vendor_mgmt'), label: 'Vendor registration & document collection' },
    { condition: (sel) => sel.subjects.includes('visitors'), label: 'Website cookies & device fingerprinting' },
    { condition: (sel) => sel.subjects.includes('applicants'), label: 'Career portals & job application forms' },
    { condition: (sel) => sel.elements.includes('biometric'), label: 'Biometric capture (attendance, access control)' },
  ],
  store: [
    { condition: (sel) => sel.systems.includes('crm'), label: 'CRM databases (contacts, interactions, deals)' },
    { condition: (sel) => sel.systems.includes('hrms'), label: 'HRMS & payroll systems (employee records)' },
    { condition: (sel) => sel.systems.includes('cloud'), label: 'Cloud platform storage & backups' },
    { condition: (sel) => sel.systems.includes('saas'), label: 'Third-party SaaS application databases' },
    { condition: (sel) => sel.systems.includes('onprem'), label: 'On-premise servers & local databases' },
    { condition: (sel) => sel.systems.includes('sheets'), label: 'Spreadsheets, shared drives & email inboxes' },
    { condition: (sel) => sel.elements.includes('gov_ids'), label: 'Document management systems (ID scan copies)' },
    { condition: (sel) => sel.elements.includes('health'), label: 'Medical records repositories & insurance files' },
  ],
  use: [
    { condition: (sel) => sel.activities.includes('marketing'), label: 'Campaign targeting & audience segmentation' },
    { condition: (sel) => sel.activities.includes('loyalty'), label: 'Customer profiling & purchase analysis' },
    { condition: (sel) => sel.activities.includes('customer_kyc'), label: 'Identity verification & risk scoring' },
    { condition: (sel) => sel.activities.includes('employee_payroll'), label: 'Salary processing & statutory filings (PF, ESI)' },
    { condition: (sel) => sel.activities.includes('payments'), label: 'Transaction processing & financial reconciliation' },
    { condition: (sel) => sel.elements.includes('biometric'), label: 'Attendance tracking & physical access control' },
    { condition: (sel) => sel.elements.includes('digital'), label: 'Analytics, personalisation & user behaviour tracking' },
  ],
  share: [
    { condition: (sel) => sel.systems.includes('cloud') || sel.systems.includes('saas'), label: 'Cloud & SaaS vendors (data processors)' },
    { condition: (sel) => sel.activities.includes('vendor_mgmt'), label: 'Partner vendors, banks & payment intermediaries' },
    { condition: (sel) => sel.activities.includes('employee_payroll'), label: 'Government agencies (PF, ESI, Income Tax)' },
    { condition: (sel) => sel.activities.includes('marketing'), label: 'Ad networks, analytics & marketing platforms' },
    { condition: (sel) => sel.activities.includes('payments'), label: 'Payment gateways & acquiring banks' },
    { condition: (sel) => sel.systems.includes('sheets'), label: 'Email recipients via shared drives & attachments' },
    { condition: (sel) => sel.elements.includes('health'), label: 'Insurance providers & third-party administrators' },
  ],
};

// ──────────────────────────────────────────────────────────
// 5. CORE ENGINE FUNCTIONS
// ──────────────────────────────────────────────────────────

/**
 * Infer likely personal data categories from user selections.
 * Returns categories with source tracking and confidence levels.
 */
export function inferDataCategories(selections) {
  const { industry, activities, subjects, elements, systems } = selections;

  // Accumulate category signals: { categoryId: Set<sourceLabel> }
  const signals = {};
  const addSignal = (catId, source) => {
    if (!signals[catId]) signals[catId] = new Set();
    signals[catId].add(source);
  };

  // Industry signals
  (INDUSTRY_CATEGORY_MAP[industry] || []).forEach(cat => addSignal(cat, `Industry: ${industry}`));

  // Activity signals
  activities.forEach(act => {
    (ACTIVITY_CATEGORY_MAP[act] || []).forEach(cat => addSignal(cat, `Activity: ${act}`));
  });

  // Subject signals
  subjects.forEach(sub => {
    (SUBJECT_CATEGORY_MAP[sub] || []).forEach(cat => addSignal(cat, `Subject: ${sub}`));
  });

  // Element signals (strongest — user explicitly selected these)
  elements.forEach(el => {
    (ELEMENT_CATEGORY_MAP[el] || []).forEach(cat => addSignal(cat, `Element: ${el}`));
  });

  // System signals
  systems.forEach(sys => {
    (SYSTEM_CATEGORY_MAP[sys] || []).forEach(cat => addSignal(cat, `System: ${sys}`));
  });

  // Build results with confidence
  const results = Object.entries(signals).map(([catId, sources]) => {
    const def = DATA_CATEGORIES[catId];
    if (!def) return null;

    const sourceList = [...sources];
    const signalCount = sourceList.length;

    // Confidence: 3+ signals = high, 2 = medium, 1 = low
    const confidence = signalCount >= 3 ? 'high' : signalCount >= 2 ? 'medium' : 'low';

    // Direct = user explicitly selected an element that maps here
    const directSources = sourceList.filter(s => s.startsWith('Element:'));
    const isDirect = directSources.length > 0;

    return {
      ...def,
      confidence,
      isDirect,
      signalCount,
      sources: sourceList,
    };
  }).filter(Boolean);

  // Sort: high confidence first, then direct first
  results.sort((a, b) => {
    const confOrder = { high: 0, medium: 1, low: 2 };
    if (confOrder[a.confidence] !== confOrder[b.confidence]) return confOrder[a.confidence] - confOrder[b.confidence];
    if (a.isDirect !== b.isDirect) return a.isDirect ? -1 : 1;
    return b.signalCount - a.signalCount;
  });

  return results;
}

/**
 * Generate DPDP impact flags with severity based on selections and inferred categories.
 */
export function generateImpactFlags(selections, inferredCategories) {
  const flags = [];

  Object.entries(FLAG_TRIGGER_RULES).forEach(([flagId, rule]) => {
    if (rule.triggers(selections, inferredCategories)) {
      const def = IMPACT_FLAG_DEFS[flagId];
      const severity = rule.severity(selections, inferredCategories);
      flags.push({
        ...def,
        severity,
      });
    }
  });

  // Sort: high severity first
  const sevOrder = { high: 0, medium: 1, low: 2 };
  flags.sort((a, b) => sevOrder[a.severity] - sevOrder[b.severity]);

  return flags;
}

/**
 * Detect blind spots — gaps the user likely missed.
 */
export function detectBlindSpots(selections, inferredCategories) {
  return BLIND_SPOT_RULES
    .filter(rule => rule.condition(selections, inferredCategories))
    .sort((a, b) => {
      const ord = { high: 0, medium: 1, low: 2 };
      return ord[a.severity] - ord[b.severity];
    })
    .slice(0, 4);
}

/**
 * Compute overall DPDP impact score (0–100).
 */
export function computeImpactScore(selections, categories, flags) {
  const indRisk = { bfsi: 4, healthcare: 5, it_saas: 3, manufacturing: 2, retail: 3, education: 4 };
  const industryRisk = indRisk[selections.industry] || 2;

  let score = 0;

  // Industry weight (0–20)
  score += industryRisk * 4;

  // Category breadth (0–20): more categories = more exposure
  score += Math.min(categories.length * 2, 20);

  // High-confidence categories extra weight (0–12)
  score += categories.filter(c => c.confidence === 'high').length * 3;

  // Sensitive category multiplier (0–15)
  const hasSensitive = categories.some(c => ['sensitive', 'children', 'financial'].includes(c.id));
  if (hasSensitive) score += 15;

  // Flag severity weight (0–27)
  flags.forEach(f => {
    if (f.severity === 'high') score += 5;
    else if (f.severity === 'medium') score += 3;
    else score += 1;
  });

  // System breadth (0–9)
  score += Math.min(selections.systems.length * 3, 9);

  // SDF indicator bonus
  const isSDF = flags.some(f => f.id === 'high_volume_risk' && f.severity === 'high') ||
    flags.some(f => f.id === 'children_caution');
  if (isSDF) score += 8;

  return Math.min(Math.round(score), 100);
}

/**
 * Build a structured lead summary for CX0 platform handoff.
 */
export function buildLeadSummary(selections, categories, flags, blindSpots, score) {
  const indRisk = { bfsi: 4, healthcare: 5, it_saas: 3, manufacturing: 2, retail: 3, education: 4 };
  const industryRisk = indRisk[selections.industry] || 2;

  const isSDF = flags.some(f => f.id === 'high_volume_risk' && f.severity === 'high') ||
    flags.some(f => f.id === 'children_caution') ||
    categories.some(c => ['sensitive', 'children'].includes(c.id) && c.confidence === 'high');

  let impactLevel, impactColor;
  if (score >= 70) { impactLevel = 'Critical'; impactColor = '#DC2626'; }
  else if (score >= 50) { impactLevel = 'High'; impactColor = '#F59E0B'; }
  else if (score >= 30) { impactLevel = 'Moderate'; impactColor = '#EAB308'; }
  else { impactLevel = 'Low'; impactColor = '#22C55E'; }

  // Recommendations based on flags and categories
  const recommendations = [];
  if (isSDF) {
    recommendations.push('Your organisation likely qualifies as a Significant Data Fiduciary. Immediate steps: appoint a Data Protection Officer and initiate a Data Protection Impact Assessment.');
  }
  recommendations.push('Run the CX0 Executive Readiness Assessment to get a detailed pillar-by-pillar compliance score, gap register, and 12–18 month remediation roadmap.');
  if (categories.some(c => c.id === 'sensitive')) {
    recommendations.push('Implement encryption and strict access controls for all sensitive personal data categories — health records, biometrics, and government IDs.');
  }
  if (flags.some(f => f.id === 'processor_oversight' && f.severity === 'high')) {
    recommendations.push('Audit all cloud and SaaS vendor agreements for DPDP-compliant data processing terms, data residency clauses, and breach notification commitments.');
  }
  if (flags.some(f => f.id === 'retention_deletion' && f.severity === 'high')) {
    recommendations.push('Define retention schedules for every system storing personal data. Implement automated purge workflows with audit trails.');
  }
  if (blindSpots.length >= 3) {
    recommendations.push('Your data landscape has multiple blind spots. A comprehensive data mapping exercise should precede any compliance programme.');
  }

  return {
    score,
    impactLevel,
    impactColor,
    isSDF,
    industryRisk,
    categoryCount: categories.length,
    highConfidenceCount: categories.filter(c => c.confidence === 'high').length,
    flagsTriggered: flags.length,
    highSeverityFlags: flags.filter(f => f.severity === 'high').length,
    blindSpotCount: blindSpots.length,
    recommendations: recommendations.slice(0, 5),
    // Serialisable data for backend
    categories: categories.map(c => ({ id: c.id, label: c.label, confidence: c.confidence, isDirect: c.isDirect })),
    flags: flags.map(f => ({ id: f.id, label: f.label, severity: f.severity })),
    blindSpots: blindSpots.map(b => ({ id: b.id, severity: b.severity })),
  };
}

/**
 * Infer likely business touchpoints where personal data flows.
 */
export function inferTouchpoints(selections) {
  const result = {};
  for (const [phase, rules] of Object.entries(TOUCHPOINT_RULES)) {
    result[phase] = rules
      .filter(rule => rule.condition(selections))
      .map(rule => rule.label);
  }
  return result;
}

/**
 * Compute business visibility gap — how much of the data landscape is likely untracked.
 */
export function computeVisibilityGap(selections, categories, flags, blindSpots) {
  let gapScore = 0;
  gapScore += blindSpots.length * 12;
  if (selections.systems.length > selections.elements.length) gapScore += 8;
  if (selections.activities.length > 2 && selections.subjects.length < 2) gapScore += 10;
  if (selections.systems.includes('sheets')) gapScore += 8;
  gapScore += categories.filter(c => c.confidence === 'low').length * 5;

  const industryExpected = {
    bfsi: ['gov_ids', 'financial'],
    healthcare: ['health', 'gov_ids'],
    education: ['contact_info'],
    retail: ['financial', 'digital'],
    it_saas: ['digital', 'contact_info'],
    logistics: ['contact_info', 'digital'],
    real_estate: ['gov_ids', 'financial'],
    professional_services: ['contact_info', 'gov_ids'],
  };
  const expected = industryExpected[selections.industry] || [];
  gapScore += expected.filter(e => !selections.elements.includes(e)).length * 7;

  let level, color, description;
  if (gapScore >= 45) {
    level = 'High';
    color = '#DC2626';
    description = 'Significant gaps in your data visibility. Multiple areas where personal data is likely processed without adequate oversight or documentation.';
  } else if (gapScore >= 20) {
    level = 'Medium';
    color = '#F59E0B';
    description = 'Some blind spots exist in your data landscape. A structured data mapping exercise would uncover processing activities not yet on your radar.';
  } else {
    level = 'Low';
    color = '#22C55E';
    description = 'Relatively good visibility into your data landscape. Focus on formalising what you already know into DPDP-compliant documentation.';
  }

  return { level, color, description, score: Math.min(gapScore, 100) };
}

/**
 * Generate personalised "What to do next" checklist based on analysis results.
 */
export function generateNextSteps({ categories, flags, blindSpots, summary, selections }) {
  const steps = [];

  steps.push({
    label: 'Run the CX0 Executive Readiness Assessment',
    detail: 'Get a pillar-by-pillar compliance score, full gap register, and 12–18 month remediation roadmap.',
    priority: 'critical',
  });

  if (summary.isSDF) {
    steps.push({
      label: 'Evaluate Significant Data Fiduciary classification',
      detail: 'Your profile suggests likely SDF status \u2014 this means appointing a DPO, periodic DPIAs, and enhanced audit obligations.',
      priority: 'critical',
    });
  }

  if (blindSpots.length >= 2) {
    steps.push({
      label: 'Conduct a comprehensive data mapping exercise',
      detail: 'Map every data flow from collection to deletion across all departments and systems.',
      priority: 'high',
    });
  }

  if (flags.some(f => f.id === 'processor_oversight' && f.severity === 'high')) {
    steps.push({
      label: 'Audit vendor and processor agreements',
      detail: 'Review cloud/SaaS vendor contracts for DPDP-compliant data processing terms and breach notification clauses.',
      priority: 'high',
    });
  }

  if (categories.some(c => ['sensitive', 'children'].includes(c.id))) {
    steps.push({
      label: 'Implement enhanced safeguards for sensitive data',
      detail: "Encryption, strict access controls, and purpose-limited processing for health records, biometrics, and children's data.",
      priority: 'high',
    });
  }

  if (flags.some(f => f.id === 'retention_deletion' && (f.severity === 'high' || f.severity === 'medium'))) {
    steps.push({
      label: 'Define data retention schedules',
      detail: 'Document retention periods for every system and implement automated purge workflows with audit trails.',
      priority: 'medium',
    });
  }

  if (selections.systems.includes('sheets')) {
    steps.push({
      label: 'Audit spreadsheets and shared drives',
      detail: 'Inventory all spreadsheets containing personal data. Move to controlled systems with access logging.',
      priority: 'medium',
    });
  }

  if (flags.some(f => f.id === 'consent' && (f.severity === 'high' || f.severity === 'medium'))) {
    steps.push({
      label: 'Implement a consent management framework',
      detail: 'Deploy auditable consent collection, storage, and withdrawal mechanisms across all data processing activities.',
      priority: 'medium',
    });
  }

  return steps.slice(0, 6);
}

/**
 * Full analysis pipeline — single entry point.
 */
export function runFullAnalysis(selections) {
  const categories = inferDataCategories(selections);
  const flags = generateImpactFlags(selections, categories);
  const blindSpots = detectBlindSpots(selections, categories);
  const score = computeImpactScore(selections, categories, flags);
  const touchpoints = inferTouchpoints(selections);
  const visibilityGap = computeVisibilityGap(selections, categories, flags, blindSpots);
  const summary = buildLeadSummary(selections, categories, flags, blindSpots, score);
  const nextSteps = generateNextSteps({ categories, flags, blindSpots, summary, selections });

  return {
    categories,
    flags,
    blindSpots,
    score,
    touchpoints,
    visibilityGap,
    nextSteps,
    summary,
  };
}
